import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, ScrollText } from "lucide-react";
import { toast } from "sonner";
import LegalMarkdown from "@/components/LegalMarkdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  terms: ScrollText,
  privacy: ShieldCheck,
  hipaa: ShieldCheck,
};

const LABELS = {
  terms: "Terms of Service",
  privacy: "Privacy Notice",
  hipaa: "HIPAA Notice",
};

/**
 * Signed-in family users get this modal whenever an admin publishes a new
 * version of Terms/Privacy that they haven't yet accepted. Blocks nothing
 * until dismissed by acceptance; parent decides whether to hard-gate the app.
 *
 * Props:
 *   user            — the logged-in user object (must have email). If null,
 *                     the modal never fetches.
 *   onAccepted?     — called after successful acceptance.
 */
export default function LegalConsentModal({ user, onAccepted }) {
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!user?.email) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/legal/pending`);
        if (!alive) return;
        if (Array.isArray(data.pending) && data.pending.length > 0) {
          setPending(data.pending);
          setOpen(true);
        }
      } catch (e) {
        // Not authenticated / network — silently skip; nothing to gate.
      }
    })();
    return () => { alive = false; };
  }, [user?.email]);

  if (!open || pending.length === 0) return null;

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/legal/consent`, {
        acknowledgements: pending.map((d) => ({ doc_type: d.doc_type, version: d.version })),
        context: "reacceptance",
      });
      toast.success("Thanks — your acceptance has been recorded.");
      setOpen(false);
      if (typeof onAccepted === "function") onAccepted();
    } catch (e) {
      toast.error("We couldn't save your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="legal-consent-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-consent-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-primary-dark px-6 py-5 text-white">
          <h2 id="legal-consent-title" className="text-xl md:text-2xl font-heading font-bold">
            We've updated our {pending.length === 1 ? LABELS[pending[0].doc_type] : "legal terms"}
          </h2>
          <p className="text-sm mt-1 opacity-90 font-body">
            Please review and accept the latest {pending.length === 1 ? "version" : "versions"} to keep using Tyvena.
          </p>
        </div>

        {pending.length > 1 && (
          <div className="flex gap-2 px-6 pt-4 border-b border-[#E2E8F0]">
            {pending.map((d, idx) => {
              const Icon = ICONS[d.doc_type] || ScrollText;
              return (
                <button
                  key={d.id}
                  data-testid={`legal-tab-${d.doc_type}`}
                  onClick={() => setActiveIdx(idx)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-body font-semibold transition ${
                    activeIdx === idx
                      ? "bg-primary/10 text-primary border-b-2 border-primary"
                      : "text-text-secondary hover:text-primary"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {LABELS[d.doc_type] || d.doc_type} v{d.version}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-5 bg-[#FAFBFC]">
          {(() => {
            const active = pending[activeIdx] || pending[0];
            return (
              <div>
                <div className="text-xs text-text-secondary font-body mb-4">
                  <span className="font-semibold uppercase tracking-wide">Version {active.version}</span>
                  {active.effective_date && (
                    <> · Effective {active.effective_date}</>
                  )}
                </div>
                <LegalMarkdown body={active.body_markdown} />
              </div>
            );
          })()}
        </div>

        <div className="border-t border-[#E2E8F0] px-6 py-5 bg-white">
          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              data-testid="legal-consent-checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 w-5 h-5 accent-primary"
            />
            <span className="text-sm font-body text-text-primary">
              I have read and accept the updated{" "}
              {pending.map((d, i) => (
                <span key={d.id}>
                  <strong>{LABELS[d.doc_type] || d.doc_type} v{d.version}</strong>
                  {i < pending.length - 2 ? ", " : i === pending.length - 2 ? " and " : ""}
                </span>
              ))}
              .
            </span>
          </label>
          <div className="flex items-center justify-end gap-3">
            <Button
              data-testid="legal-consent-accept-btn"
              onClick={handleAccept}
              disabled={!checked || submitting}
              className="bg-primary hover:bg-primary-dark text-white rounded-full px-6 font-body font-semibold"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <>Accept &amp; continue</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
